import { IS_NATIVE_TAURI_CLIPBOARD_ENABLED } from '@/app-shell/flags/desktop-flags';
import { isClipboardWriteRecoveryKilled } from '@/app-shell/flags/session-flags';
import { reportSectionMarkerResidue } from '@/components/tiptap/shared/section-residue';
import { type CopySettings } from '@/features/copySettings';
import {
  getDesktopClipboardSnapshot,
  type DesktopClipboardSnapshot,
} from '@/features/copySettings/rtf-copy-state';
import {
  removeAnnotationFromMarkdown,
  simpleHtmlToMarkdown,
} from '@/helpers/htmlToMarkdown';
import { reformatHtml, simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { RtfClipboardError } from '@/tauri/controller/clipboard';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { stripEmptySectionHeadingPresentationHtml } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { ClipboardItem, write, type ClipboardItemInterface } from 'clipboard-polyfill';
import { describeClipboardError } from './describeClipboardError';
import { isWebKitEngine } from './isWebKitEngine';
import {
  resolveClipboardPlainText,
  resolvePlainCopyMarkdown,
} from './plainTextClipboard';
import { removeListMarkers } from './removeListMarkers';

let pendingWrite: Promise<void> | null = null;
let safariWriteGeneration = 0;
// Exported so tests derive attempt counts instead of hardcoding them.
export const CLIPBOARD_MAX_RETRIES = 2;
export const CLIPBOARD_RETRY_DELAY_MS = 150;
/**
 * How long a failed write waits for the window to come back before giving up.
 * Long enough for a focus handoff (an OS prompt, a native window taking and
 * returning focus), short enough that a user who has genuinely moved on to
 * another app gets the failure while the copy is still what they were doing.
 */
export const CLIPBOARD_REFOCUS_WAIT_MS = 2000;

type ClipboardWriteJob =
  | {
      kind: 'ordinary';
      items: ClipboardItemInterface[];
      onSuperseded?: () => void;
    }
  | {
      kind: 'desktopNative';
      items: ClipboardItemInterface[];
      snapshot: DesktopClipboardSnapshot;
    };

type QueuedClipboardWrite = {
  payload: ClipboardWriteJob;
  resolve: () => void;
  reject: (reason: unknown) => void;
};

// A desktop-native chain must also wait for older Safari jobs whose tracking
// owner was replaced. Browser-only writes retain their existing gesture behavior.
const ordinaryWritesInFlight = new Set<Promise<void>>();
let desktopNativeBarrier = false;
const trackOrdinaryWrite = (job: Promise<void>): Promise<void> => {
  const settled = job.then(
    () => {},
    () => {}
  );
  ordinaryWritesInFlight.add(settled);
  void settled.then(() => ordinaryWritesInFlight.delete(settled));
  return job;
};

const settleSupersededWrite = (queued: QueuedClipboardWrite): void => {
  if (queued.payload.kind === 'desktopNative') {
    queued.reject(new RtfClipboardError('CLIPBOARD_SUPERSEDED'));
  } else {
    queued.payload.onSuperseded?.();
    queued.resolve();
  }
};

/** Latest payload waiting behind an in-flight write; unpublished native jobs reject. */
let latestQueuedWrite: QueuedClipboardWrite | null = null;

/** Test-only: drop in-flight/coalesced state so one leaky case can't coalesce the next. */
export function __resetClipboardWriteStateForTests(): void {
  pendingWrite = null;
  latestQueuedWrite = null;
  desktopNativeBarrier = false;
  ordinaryWritesInFlight.clear();
}

/**
 * Gesture / permission failures are not transient. Delayed retries run outside
 * the user-activation window and reject with the same NotAllowedError.
 * Duck-type `name` rather than `instanceof DOMException` — the prototype is not
 * reliably Error across WebViews (see describeClipboardError).
 *
 * Production note: clipboard-polyfill catches any native `write()` rejection
 * for a text/plain|text/html payload and falls back to execCommand, rethrowing
 * a plain `Error` — so this predicate only fires when the polyfill is bypassed
 * or mocked. It is kept as a fail-fast guard for that direct-rejection shape.
 */
const isNotAllowedClipboardError = (err: unknown): boolean =>
  (err as { name?: unknown } | null)?.name === 'NotAllowedError';

/**
 * The condition Chromium checks before an async clipboard write: an unfocused
 * document is refused outright with `NotAllowedError: Document is not focused`.
 * A runtime without the API is treated as focused so the write is still tried.
 */
const documentHasFocus = (): boolean =>
  typeof document === 'undefined' || typeof document.hasFocus !== 'function'
    ? true
    : document.hasFocus();

/**
 * Resolves true once the document is focused, false if the wait expires while
 * it is still not. The `focus` event alone is not enough to decide: focus can
 * be regained before the listener is attached, and a document can be focused
 * without this window seeing an event at all, so the condition itself is read
 * on entry, again after subscribing, and once more at the timeout.
 */
const waitForDocumentFocus = (): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    if (documentHasFocus()) {
      resolve(true);
      return;
    }
    const listener = new AbortController();
    const timer = setTimeout(() => {
      listener.abort();
      resolve(documentHasFocus());
    }, CLIPBOARD_REFOCUS_WAIT_MS);
    const settle = (focused: boolean) => {
      clearTimeout(timer);
      listener.abort();
      resolve(focused);
    };
    // Not `once`: a focus event that arrives while the document still is not
    // focused must not end the wait, so the listener stays armed until either
    // the condition actually holds or the timeout reads it one last time.
    window.addEventListener(
      'focus',
      () => {
        if (documentHasFocus()) settle(true);
      },
      { signal: listener.signal }
    );
    // Focus regained between the check above and this subscription would
    // otherwise burn the full wait against an already-focused document.
    if (documentHasFocus()) settle(true);
  });

/**
 * Reported in place of the browser's `NotAllowedError` when the write failed
 * with the document unfocused. Chromium raises that same name for an expired
 * user activation, and the two want opposite fixes — waiting for the window
 * versus writing earlier in the gesture — so the unfocused cohort carries its
 * own name instead of being read as one more gesture failure.
 */
class ClipboardNotFocusedError extends Error {
  constructor() {
    super('clipboard write failed while the document was unfocused');
    this.name = 'ClipboardNotFocusedError';
  }
}

/**
 * Native OS clipboard write for desktop (Tauri), used only after the web
 * clipboard path has fully failed. Kept as a fallback by design: the native
 * pipeline can produce different paste formatting in some EHR integrations, so
 * it must never pre-empt the web path.
 */
async function writeViaNativeTauriClipboard(
  items: ClipboardItemInterface[],
  isCurrent: () => boolean
): Promise<boolean> {
  const item = items[0];
  if (!item) {
    throw new Error('no clipboard item to rescue');
  }
  // Load the plugin first, then read the payload, so the guard below is after
  // every yield in this function rather than in the middle of them. Ordered
  // this way a test that holds either read also holds the whole window, and no
  // future reordering can quietly open a gap between the check and the write.
  const { writeHtml, writeText } = await import('@tauri-apps/plugin-clipboard-manager');
  const hasHtml = Array.from(item.types ?? []).includes('text/html');
  const plain = await (await item.getType('text/plain')).text();
  const html = hasHtml ? await (await item.getType('text/html')).text() : undefined;
  // A newer copy can have landed its own note on the OS clipboard while the
  // above was resolving, and writing now would replace it with this older one
  // — the stale paste this path exists to prevent.
  if (!isCurrent()) {
    return false;
  }
  if (html !== undefined) {
    // altText writes text/plain in the same atomic operation — a follow-up
    // writeText would clear the freshly written HTML.
    await writeHtml(html, plain);
  } else {
    await writeText(plain);
  }
  return true;
}

/**
 * Outcome of the desktop rescue. `failed` carries the plugin's own error so a
 * real native-clipboard defect is reported as itself rather than being folded
 * into whatever the web path last threw.
 */
type TauriRescueOutcome =
  | { status: 'rescued' }
  | { status: 'failed'; error: unknown }
  | { status: 'superseded' }
  | { status: 'unavailable' };

/**
 * Run the OS clipboard rescue, shared by the polyfill path and the Safari
 * native path — a WebKit runtime inside Tauri has the same OS plugin, and a
 * failed web write there leaves the clipboard stale for the EHR paste.
 * `available` is passed in so the flag is read once per write; `isCurrent`
 * is re-read just before the plugin write, since everything ahead of it
 * yields (see writeViaNativeTauriClipboard).
 */
async function rescueViaNativeTauriClipboard(
  items: ClipboardItemInterface[],
  available: boolean,
  isCurrent: () => boolean = () => true
): Promise<TauriRescueOutcome> {
  if (!available) {
    return { status: 'unavailable' };
  }
  try {
    if (!(await writeViaNativeTauriClipboard(items, isCurrent))) {
      return { status: 'superseded' };
    }
    logger.warn('[clipboard] native fallback rescued', {
      mimeTypes: items.flatMap((item) => Array.from(item.types ?? [])),
      isTauri: true,
    });
    return { status: 'rescued' };
  } catch (nativeError) {
    logger.warn('[clipboard] native fallback failed', {
      ...describeClipboardError(nativeError),
      isTauri: true,
    });
    return { status: 'failed', error: nativeError };
  }
}

async function executeClipboardWrite(items: ClipboardItemInterface[]): Promise<void> {
  let lastError: unknown;
  let attempts = 0;
  for (let attempt = 0; attempt <= CLIPBOARD_MAX_RETRIES; attempt++) {
    attempts = attempt + 1;
    try {
      await write(items);
      return;
    } catch (err) {
      lastError = err;
      if (isNotAllowedClipboardError(err) || attempt >= CLIPBOARD_MAX_RETRIES) {
        break;
      }
      await new Promise<void>((r) => setTimeout(r, CLIPBOARD_RETRY_DELAY_MS));
    }
  }
  // Also fires for a non-retryable NotAllowedError (attempts: 1). Callers only
  // toast and swallow, so this warn is the web-path failure canary — emit it
  // before any rescue attempt.
  logger.warn('[clipboard] write failed after retries', {
    ...describeClipboardError(lastError),
    attempts,
    isTauri: isTauri(),
    documentFocused: documentHasFocus(),
    mimeTypes: items.flatMap((item) => Array.from(item.types ?? [])),
    userActivationActive:
      typeof navigator !== 'undefined' ? navigator.userActivation?.isActive : undefined,
  });

  const tauriRescueAvailable = isTauri() && IS_NATIVE_TAURI_CLIPBOARD_ENABLED();

  // One direct attempt at the native API, which does two things the polyfill
  // cannot. It writes if whatever blocked the retries has since cleared. And it
  // keeps the rejection: for a text/plain|text/html payload the polyfill
  // swallows the native DOMException, drops to execCommand and rethrows a bare
  // `write() failed`, which is why these failures reach RUM under the
  // undiagnosable name `Error`.
  let stillUnfocused = false;
  if (canNativeClipboardWrite() && !isClipboardWriteRecoveryKilled()) {
    const failedUnfocused = !documentHasFocus();
    if (failedUnfocused && tauriRescueAvailable) {
      // Desktop's native clipboard needs no document focus, so it can rescue
      // this write now. Holding for the window first would leave the OS
      // clipboard stale exactly while the user is pasting into the EHR.
      stillUnfocused = true;
    } else {
      // Chrome and Edge refuse an async write from an unfocused document, and
      // the polyfill's execCommand fallback cannot write for one either — so
      // every attempt above was spent against a condition only the window
      // coming back can clear. Wait for it, bounded, rather than failing a copy
      // the user is not even looking at yet.
      const refocused = failedUnfocused ? await waitForDocumentFocus() : true;
      if (refocused) {
        try {
          await nativeClipboardWrite(items);
          logger.warn('[clipboard] native web write rescued', {
            mimeTypes: items.flatMap((item) => Array.from(item.types ?? [])),
            isTauri: isTauri(),
            refocused: failedUnfocused,
          });
          return;
        } catch (nativeError) {
          lastError = nativeError;
        }
      } else {
        stillUnfocused = true;
      }
    }
  }

  const rescue = await rescueViaNativeTauriClipboard(items, tauriRescueAvailable);
  if (rescue.status === 'rescued') {
    return;
  }
  if (rescue.status === 'failed' && stillUnfocused) {
    // The plugin ran and broke, and there is no real web error to report here
    // — the skip-wait path never took a native attempt. Raising the synthetic
    // ClipboardNotFocusedError would file a native-clipboard defect under the
    // unfocused cohort this name exists to isolate. Where the web path *did*
    // produce an error, that first cause still wins.
    throw rescue.error;
  }
  throw stillUnfocused ? new ClipboardNotFocusedError() : lastError;
}

/**
 * True when the native async Clipboard API can receive a synchronous,
 * Promise-valued `write()`. Reads the *native* global `ClipboardItem`, not the
 * clipboard-polyfill import that shadows it at the top of this module.
 */
const canNativeClipboardWrite = (): boolean =>
  typeof globalThis !== 'undefined' &&
  typeof globalThis.ClipboardItem !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.clipboard?.write === 'function';

/**
 * Issue a native `navigator.clipboard.write()` synchronously, in the same task
 * as the caller. Each type's Blob is handed over as the already-resolved
 * Promise from `getType()` — WebKit accepts Promise-valued `ClipboardItem`
 * entries and keeps the transient user activation as long as `write()` itself
 * is called synchronously (no `await` / `.then()` before it).
 *
 * Also used as the last web rescue in `executeClipboardWrite`, where what
 * matters is the rejection rather than the timing: going straight at the API
 * keeps the real DOMException that clipboard-polyfill would have swallowed.
 */
const nativeClipboardWrite = (items: ClipboardItemInterface[]): Promise<void> => {
  const NativeClipboardItem = globalThis.ClipboardItem;
  const nativeItems = items.map((item) => {
    const record: Record<string, Promise<Blob>> = {};
    for (const type of item.types) {
      record[type] = item.getType(type);
    }
    return new NativeClipboardItem(record);
  });
  return navigator.clipboard.write(nativeItems);
};

/** Drain one coalesced successor, or mark the polyfill chain idle. */
const drainLatestQueuedWrite = (): void => {
  const queued = latestQueuedWrite;
  latestQueuedWrite = null;
  if (queued) {
    const next = startSerializedClipboardWrite(queued.payload);
    void next.then(queued.resolve, queued.reject);
  } else {
    pendingWrite = null;
    desktopNativeBarrier = false;
  }
};

async function executeDesktopNativeJob(
  payload: Extract<ClipboardWriteJob, { kind: 'desktopNative' }>
): Promise<void> {
  await Promise.all(ordinaryWritesInFlight);
  const { writeDesktopRtfClipboard } = await import('./desktop-rtf-clipboard');
  await writeDesktopRtfClipboard(payload.items, payload.snapshot);
}

/**
 * Start a polyfill write and, when it settles, drain at most one coalesced
 * successor. `pendingWrite` stays set until the chain is idle so a click that
 * arrives in the settle window cannot overlap the drain (Win32/Epic).
 */
const startSerializedClipboardWrite = (payload: ClipboardWriteJob): Promise<void> => {
  const job =
    payload.kind === 'desktopNative'
      ? executeDesktopNativeJob(payload)
      : trackOrdinaryWrite(
          desktopNativeBarrier
            ? Promise.all(ordinaryWritesInFlight).then(() =>
                executeClipboardWrite(payload.items)
              )
            : executeClipboardWrite(payload.items)
        );
  const chain: Promise<void> = job
    .catch(() => {})
    .finally(() => {
      // A Safari native write can replace pendingWrite while this polyfill
      // write is still in-flight — only the current owner may drain, or a
      // queued write could overlap the Safari one.
      if (pendingWrite === chain) {
        drainLatestQueuedWrite();
      }
    });
  pendingWrite = chain;
  return job;
};

/**
 * Latest-wins serialisation for the polyfill path. One write in-flight; extra
 * clicks replace a single queued payload rather than FIFO-chaining every click
 * (a rage-click storm otherwise retries after the gesture is gone). The idle
 * call runs in this tick so user activation is preserved. A coalesced
 * successor runs after the in-flight write settles and may be outside the
 * gesture; the polyfill surfaces that as a plain `Error` (see the
 * serializedClipboardWrite docblock), so the NotAllowedError fail-fast in
 * executeClipboardWrite does not fire for it in production.
 * Ordinary superseded waiters resolve (Safari-style). Native waiters
 * reject rather than claim that an unpublished copy succeeded.
 */
const enqueueLatestClipboardWrite = (payload: ClipboardWriteJob): Promise<void> => {
  if (!pendingWrite) {
    return startSerializedClipboardWrite(payload);
  }
  if (latestQueuedWrite) {
    settleSupersededWrite(latestQueuedWrite);
  }
  return new Promise<void>((resolve, reject) => {
    latestQueuedWrite = { payload, resolve, reject };
  });
};

/**
 * Serialised clipboard write with retry. `onSuperseded` optionally signals an
 * unpublished ordinary attempt; its promise still resolves for legacy callers.
 * Desktop-native supersession retains its CLIPBOARD_SUPERSEDED rejection.
 *
 * On Windows the OS clipboard is a shared resource that must be
 * opened → written → closed atomically.  When another application
 * (e.g. Epic) holds the clipboard open, `navigator.clipboard.write()`
 * rejects and clipboard-polyfill falls back to `document.execCommand`.
 * Overlapping fallback calls corrupt the Win32 clipboard viewer chain,
 * which permanently breaks paste until the machine is rebooted.
 *
 * This wrapper:
 *  1. Keeps one polyfill write in-flight and coalesces extra clicks to the
 *     latest payload (prevents overlapping fallbacks without FIFO-ing a
 *     rage-click storm outside the user gesture).
 *  2. Retries non-gesture failures with a short delay, giving Epic time to
 *     release its clipboard lock. The retry loop also carries a
 *     `NotAllowedError` fail-fast, but note clipboard-polyfill wraps any
 *     native rejection for a text/plain|text/html payload into its
 *     execCommand fallback and rethrows a plain `Error`, so that branch is
 *     unreachable in production — coalescing (1) is what actually bounds a
 *     rage-click storm.
 *  3. Falls back, once the retries are spent, to the native API directly,
 *     preceded by a bounded wait for the window when the failure happened
 *     unfocused — skipped on desktop, where the OS plugin needs no focus and
 *     holding would leave the clipboard stale mid-paste. That native attempt
 *     is the only place the real rejection survives the polyfill. The OS
 *     plugin rescue runs after it and is the last chance to write on desktop.
 *     `kill-scribe-copy-clipboard-recovery` skips the Safari write and this
 *     native/refocus recovery; the plugin rescue is not gated by it.
 *
 * User-activation note: when no write is in-flight the call runs
 * synchronously (no `.then()` deferral), preserving transient user
 * activation for `navigator.clipboard.write()`.  A coalesced successor
 * may run outside its originating gesture; the polyfill then fails through
 * its execCommand fallback as a plain `Error`, which is still retried.
 */
export const serializedClipboardWrite = (
  items: ClipboardItemInterface[],
  onSuperseded?: () => void
): Promise<void> => {
  const snapshot = getDesktopClipboardSnapshot();
  if (snapshot !== null) {
    desktopNativeBarrier = true;
    return enqueueLatestClipboardWrite({ kind: 'desktopNative', items, snapshot });
  }
  // A later browser write cannot pre-empt an in-flight native publication.
  if (desktopNativeBarrier)
    return enqueueLatestClipboardWrite({ kind: 'ordinary', items, onSuperseded });

  // WebKit path. WebKit does not reliably honour the polyfill's
  // out-of-gesture `execCommand` fallback, so a write queued behind
  // `pendingWrite` silently fails and the user pastes stale clipboard content.
  // Issue the native write synchronously inside the gesture instead — no
  // `pendingWrite` chaining, no `.then()` deferral before `write()`. This is a
  // single native attempt: retrying through the polyfill would land outside the
  // gesture. WebKit rejects an older pending write when a newer write starts,
  // so superseded rejections are ignored instead of replaying stale content.
  // A failure of the latest write propagates to the caller's error UI.
  if (
    isWebKitEngine() &&
    canNativeClipboardWrite() &&
    !isClipboardWriteRecoveryKilled()
  ) {
    // A polyfill write coalesced behind the previous owner predates this
    // native write; resolve-and-clear it so it cannot drain later and
    // overwrite this newer payload (latest-wins across the path boundary).
    if (latestQueuedWrite) {
      settleSupersededWrite(latestQueuedWrite);
      latestQueuedWrite = null;
    }
    // Read once per write so a mid-write flag flip cannot make the two exits
    // below disagree about whether the desktop rescue exists.
    const tauriRescueAvailable = isTauri() && IS_NATIVE_TAURI_CLIPBOARD_ENABLED();
    let nativeJob: Promise<void>;
    try {
      nativeJob = trackOrdinaryWrite(nativeClipboardWrite(items));
    } catch (error) {
      // Same canary as the async path: a synchronous construction throw (e.g.
      // older WebKit rejecting an unsupported MIME type) bypasses
      // executeClipboardWrite, so without this warn there is no telemetry.
      logger.warn('[clipboard] safari native write construction failed', {
        ...describeClipboardError(error),
        isTauri: isTauri(),
        mimeTypes: items.flatMap((item) => Array.from(item.types ?? [])),
        userActivationActive:
          typeof navigator !== 'undefined'
            ? navigator.userActivation?.isActive
            : undefined,
      });
      const constructionError = error instanceof Error ? error : new Error(String(error));
      if (!tauriRescueAvailable) {
        return Promise.reject(constructionError);
      }
      // No write ever started, so the clipboard still holds the previous note,
      // and on desktop the OS plugin can still put the right one there. That
      // rescue IS a write to the OS clipboard, so it takes a generation and
      // owns `pendingWrite` exactly like an issued native write: without both,
      // two construction throws in a row would rescue concurrently and could
      // land out of order, leaving the older note on the clipboard. Waiting on
      // the previous chain keeps an in-flight native write from landing after
      // the plugin. Only reached when the plugin exists — with nothing to
      // order against, the caller's error must not be held behind another
      // write (see the early return above).
      const writeGeneration = ++safariWriteGeneration;
      const inFlight = pendingWrite ?? Promise.resolve();
      const rescueJob = inFlight
        .catch(() => {})
        .then(() =>
          rescueViaNativeTauriClipboard(
            items,
            true,
            () => writeGeneration === safariWriteGeneration
          )
        )
        .then((rescue) => {
          if (rescue.status === 'rescued') return;
          if (rescue.status === 'superseded') {
            onSuperseded?.();
            return;
          }
          throw constructionError;
        });

      const trackedRescue = rescueJob.catch(() => {});
      pendingWrite = trackedRescue;
      void trackedRescue.finally(() => {
        if (pendingWrite === trackedRescue) {
          drainLatestQueuedWrite();
        }
      });
      return rescueJob;
    }
    // Bump only after the write is issued: a construction that throws with no
    // plugin to rescue it never started a write, so the previous in-flight
    // write is NOT superseded (the desktop rescue above takes its own
    // generation, because it does write).
    const writeGeneration = ++safariWriteGeneration;

    const job = nativeJob.catch(async (error: unknown) => {
      if (writeGeneration === safariWriteGeneration) {
        // This branch bypasses executeClipboardWrite, so its retry-exhausted
        // warn never fires — without this a native failure leaves no telemetry
        // at all at call sites whose catch only toasts.
        logger.warn('[clipboard] safari native write failed', {
          ...describeClipboardError(error),
          isTauri: isTauri(),
          mimeTypes: items.flatMap((item) => Array.from(item.types ?? [])),
          userActivationActive:
            typeof navigator !== 'undefined'
              ? navigator.userActivation?.isActive
              : undefined,
        });
        // Re-read the generation inside the rescue, not just here: a newer
        // copy that starts while the plugin is loading owns the clipboard, and
        // a superseded rescue resolves silently rather than toasting over it.
        const rescue = await rescueViaNativeTauriClipboard(
          items,
          tauriRescueAvailable,
          () => writeGeneration === safariWriteGeneration
        );
        if (rescue.status === 'rescued') return;
        if (rescue.status === 'superseded') {
          onSuperseded?.();
          return;
        }
        throw error;
      }
      onSuperseded?.();
    });

    // A later Safari caller replaces `pendingWrite`; only the tracked job that
    // is still current may drain. Drain (don't just null) so a polyfill write
    // queued behind this native write cannot hang.
    const trackedJob = job.catch(() => {});
    pendingWrite = trackedJob;
    void trackedJob.finally(() => {
      if (pendingWrite === trackedJob) {
        drainLatestQueuedWrite();
      }
    });
    return job;
  }

  return enqueueLatestClipboardWrite({ kind: 'ordinary', items, onSuperseded });
};

type ClipboardPayload = {
  html?: string;
  markdown: string;
};

/**
 * Write to clipboard using clipboard-polyfill. Always writes text/plain;
 * text/html is included only when `html` is provided. Callers omit `html`
 * to match the legacy `copyPlainTextV2` contract (no HTML payload, so paste
 * targets like Word/Epic can't pick up default paragraph styling). Defaults to
 * the legacy void result, including ordinary supersession. Opt into
 * `trackPublication` to receive COPY_SUPERSEDED for superseded, unpublished
 * ordinary attempts. Published attempts resolve undefined; genuine failures reject.
 * A desktop-native RTF write still rejects with
 * `RtfClipboardError('CLIPBOARD_SUPERSEDED')` when superseded. Treat both as a
 * silent cancellation: compare the resolved value to COPY_SUPERSEDED, and catch
 * the rejection with `isSupersededCopyError` (which matches only the rejection).
 *
 * The text/plain bytes are resolved via `resolveClipboardPlainText`: when the
 * rollout is ON, markdown markers are stripped and line endings become CRLF —
 * plain-only calls included; the markdown-native blank-line marker is turned
 * back into blank lines either way. Rollout OFF stays byte-identical to the
 * raw markdown.
 */
export function writeHtmlAndMarkdownToClipboard(
  payload: ClipboardPayload & { trackPublication: true }
): Promise<ClipboardWriteOutcome>;
export function writeHtmlAndMarkdownToClipboard(
  payload: ClipboardPayload & { trackPublication?: false }
): Promise<void>;
export function writeHtmlAndMarkdownToClipboard(
  payload: ClipboardPayload & { trackPublication?: boolean }
): Promise<ClipboardWriteOutcome>;
// `| void` stays on the implementation only, to stay compatible with the
// Promise<void> overload.
export async function writeHtmlAndMarkdownToClipboard({
  html,
  markdown,
  trackPublication = false,
}: ClipboardPayload & {
  trackPublication?: boolean;
}): Promise<ClipboardWriteOutcome | void> {
  const plainText = resolveClipboardPlainText({ markdown });
  const payload: Record<string, Blob> = {};
  if (html != null) {
    // WebKit preserves this order as the pasteboard's fidelity priority, so
    // rich copies must advertise HTML before their plain-text fallback.
    payload['text/html'] = new Blob([html], { type: 'text/html' });
  }
  payload['text/plain'] = new Blob([plainText], { type: 'text/plain' });
  // Scanned here rather than at the call sites, which never hold the final
  // payload. Every caller is a copy affordance — export and print scan at
  // their own final points and never route through this function.
  // The text flavor scans the pre-strip markdown: the stripper never removes
  // marker text, but it drops the `# ` shape the draft-sentinel rule keys on.
  reportSectionMarkerResidue({
    payloads: [
      ...(html != null ? [{ flavor: 'html' as const, value: html }] : []),
      { flavor: 'text' as const, value: markdown },
    ],
    surface: 'copy',
  });
  let superseded = false;
  await serializedClipboardWrite(
    [new ClipboardItem(payload)],
    trackPublication
      ? () => {
          superseded = true;
        }
      : undefined
  );
  return superseded ? COPY_SUPERSEDED : undefined;
}

/**
 * Returned by a copy helper that wrote nothing to the clipboard because it had
 * no editor to serialize. Callers must not report success for it — a tick or a
 * `session_copied` on this outcome claims a copy that never landed.
 */
export const NOTHING_TO_COPY = 'nothing-to-copy' as const;

/**
 * Returned by a publication-tracking write when a newer copy replaced it before
 * it reached the clipboard. The note had content, so callers must treat it as
 * a silent cancellation like `isSupersededCopyError`: no success, no failure,
 * no empty-copy report.
 */
export const COPY_SUPERSEDED = 'copy-superseded' as const;

/** Result of a publication-tracking clipboard write. */
export type ClipboardWriteOutcome = typeof COPY_SUPERSEDED | undefined;

export type CopyOutcome = typeof NOTHING_TO_COPY | undefined;

export const copyPlainTextV2 = async (
  editor: Editor | null,
  clinicalCodesMarkdown?: string
): Promise<CopyOutcome> => {
  // Without an editor this used to write an empty string, clobbering whatever
  // the user had on the clipboard and still reporting a copy.
  if (!editor) return NOTHING_TO_COPY;
  const html = stripEmptySectionHeadingPresentationHtml(editor.getHTML());
  const markdown = simpleHtmlToMarkdown({ html, isCopy: true });
  const markdownWithoutAnnotation = resolvePlainCopyMarkdown(markdown);
  // Append clinical codes if provided
  const fullMarkdown = clinicalCodesMarkdown
    ? markdownWithoutAnnotation + clinicalCodesMarkdown
    : markdownWithoutAnnotation;

  await writeHtmlAndMarkdownToClipboard({ markdown: fullMarkdown });
};

export const copyWithFullFormatV2 = async (
  editor: Editor | null,
  clinicalCodesMarkdown?: string
): Promise<CopyOutcome> => {
  if (!editor) return NOTHING_TO_COPY;
  const htmlContent = stripEmptySectionHeadingPresentationHtml(editor.getHTML());
  const markdown = simpleHtmlToMarkdown({ html: htmlContent, isCopy: true });

  // Append clinical codes to both HTML and markdown
  const fullMarkdown = clinicalCodesMarkdown
    ? markdown + clinicalCodesMarkdown
    : markdown;
  const clinicalCodesHtml = clinicalCodesMarkdown
    ? simpleMarkdownToHtml({ markdown: clinicalCodesMarkdown })
    : '';
  const fullHtml = htmlContent + clinicalCodesHtml;
  const reformattedHtml = reformatHtml(fullHtml);

  await writeHtmlAndMarkdownToClipboard({
    html: reformattedHtml,
    markdown: fullMarkdown,
  });
};

export const copyWithFullFormatWithoutListsV2 = async (
  editor: Editor | null,
  clinicalCodesMarkdown?: string
): Promise<CopyOutcome> => {
  if (!editor) return NOTHING_TO_COPY;
  const htmlContent = stripEmptySectionHeadingPresentationHtml(editor.getHTML());
  const markdown = simpleHtmlToMarkdown({ html: htmlContent, isCopy: true });
  const markdownWithoutListMarkers = removeListMarkers(markdown);

  // Append clinical codes (clinical codes don't have list markers, so append directly)
  const fullMarkdown = clinicalCodesMarkdown
    ? markdownWithoutListMarkers + clinicalCodesMarkdown
    : markdownWithoutListMarkers;
  const html = simpleMarkdownToHtml({ markdown: fullMarkdown });
  const reformattedHtml = reformatHtml(html);

  await writeHtmlAndMarkdownToClipboard({
    html: reformattedHtml,
    markdown: fullMarkdown,
  });
};

/**
 * Unwrap an element: replace it with its own children inside its parent.
 *
 * Used for "strip the wrapper, keep the text" transforms (e.g. unwrap
 * `<strong>` while keeping the inner text in place).
 */
// The pure DOM copy-settings transforms moved into `@heidi/editor/core`
// (task 8.2; this module's census disposition is `split`). They are imported
// AND re-exported: this file still calls them below, and a bare re-export does
// not bind the name in module scope.
import {
  applyCopySettingsToHtml,
  applyCopySettingsToLegacyEditorHtml,
  isFormattingOff,
} from '@heidi/editor/core';

export { applyCopySettingsToHtml, applyCopySettingsToLegacyEditorHtml, isFormattingOff };

/**
 * Build the `{ html, markdown }` payload pair that `copyWithSettingsV2`
 * writes to the clipboard, without doing the clipboard write itself.
 *
 * Extracted so the legacy-editor export pipeline (PDF/DOCX) can produce HTML
 * that honours the same four-toggle contract as copy, including that editor's
 * historical `<p>- text</p>` bullet representation. Canonical markdown uses
 * `applyCopySettingsToHtml` directly because its lists are semantic and an
 * escaped dash paragraph must remain literal.
 *
 * Returned HTML is *not* run through `reformatHtml` — that step inlines
 * borders + collapses paragraphs for Word/Epic paste compatibility, and
 * is irrelevant (and slightly lossy) for a server-side HTML renderer.
 * Callers that need the paste-friendly variant should reformat the
 * returned `html` themselves.
 */
export const transformHtmlWithSettings = ({
  editorHtml,
  settings,
  clinicalCodesMarkdown,
}: {
  editorHtml: string;
  settings: CopySettings;
  clinicalCodesMarkdown?: string;
}): { html: string; markdown: string } => {
  const formattingOff = isFormattingOff(settings);

  const transformedHtml = applyCopySettingsToLegacyEditorHtml(
    stripEmptySectionHeadingPresentationHtml(editorHtml),
    settings
  );
  const rawMarkdown = simpleHtmlToMarkdown({
    html: transformedHtml,
    isCopy: true,
    // Copy mode intentionally preserves semantic list HTML for rich payloads.
    // The bullets-only preference writes text/plain, so ask the serializer for
    // markdown list markers instead of leaking `<ul><li>` into plain text.
    ...(formattingOff && settings.bulletPoints ? { options: { lists: true } } : {}),
  });

  // The source-aware structural pass removes semantic lists and legacy literal
  // bullet paragraphs without touching dash-prefixed code or table cells. A
  // second line-oriented marker pass would discard that provenance and corrupt
  // those protected values, so the serializer output is already canonical here.
  // Formatting-off, not all-off, so the returned markdown flavor gets the
  // same cleanup as `copyPlainTextV2`.
  let cleanedMarkdown = rawMarkdown;
  if (formattingOff) {
    cleanedMarkdown = removeAnnotationFromMarkdown(cleanedMarkdown);
  }

  const fullMarkdown = clinicalCodesMarkdown
    ? cleanedMarkdown + clinicalCodesMarkdown
    : cleanedMarkdown;

  // Bullets on: keep the DOM-transformed HTML so anchors / custom classes
  // that don't round-trip through markdown survive. Bullets off: rebuild
  // from save-path markdown of that HTML so <u> and table-cell structure
  // survive (the copy flavor flattens cell-internal <br> / escaped pipes).
  // When every formatting toggle is off, the rebuild
  // source additionally gets the markdown flavor's literal ** / _ marker
  // strip, so the re-parse cannot resurrect formatting on the all-off
  // cohort.
  let baseHtml = transformedHtml;
  if (!settings.bulletPoints) {
    const savePathMarkdown = simpleHtmlToMarkdown({ html: transformedHtml });
    baseHtml = simpleMarkdownToHtml({
      markdown: formattingOff
        ? removeAnnotationFromMarkdown(savePathMarkdown)
        : savePathMarkdown,
    });
  }
  const clinicalCodesHtml = clinicalCodesMarkdown
    ? simpleMarkdownToHtml({ markdown: clinicalCodesMarkdown })
    : '';
  const fullHtml = baseHtml + clinicalCodesHtml;

  return { html: fullHtml, markdown: fullMarkdown };
};

/**
 * Per-toggle replacement for the legacy 3-way `copyWithFullFormatV2` /
 * `copyWithFullFormatWithoutListsV2` / `copyPlainTextV2` trio. Routes the
 * editor HTML through `transformHtmlWithSettings`, then writes the
 * paste-friendly variant (`reformatHtml` applied) to the clipboard
 * alongside its markdown sibling. When every formatting toggle (headings,
 * bold/italics, tables) is off the HTML payload is omitted — paste targets
 * like Word/Epic that prefer HTML would otherwise apply default styling
 * that visually differs from a true plain-text paste. Bullets don't count:
 * they are plain-text markers.
 */
export const copyWithSettingsV2 = async (
  editor: Editor | null,
  settings: CopySettings,
  clinicalCodesMarkdown?: string
): Promise<CopyOutcome> => {
  if (!editor) return NOTHING_TO_COPY;

  const { html, markdown } = transformHtmlWithSettings({
    editorHtml: editor.getHTML(),
    settings,
    clinicalCodesMarkdown,
  });

  // Formatting off ⇒ text/plain only, matching `copyPlainTextV2`'s contract.
  if (isFormattingOff(settings)) {
    await writeHtmlAndMarkdownToClipboard({ markdown });
    return;
  }

  await writeHtmlAndMarkdownToClipboard({
    html: reformatHtml(html),
    markdown,
  });
};
